import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes, type Ref } from "react";

import { cn } from "@/lib/utils";

/**
 * The card primitive — and, as of VISUAL_SYSTEM_PLAN U3.2, the only place the
 * card recipe is written.
 *
 * Before this unit there were two card recipes in the app that nobody had
 * decided between: this primitive's `rounded-lg border bg-card
 * text-card-foreground shadow-sm`, used by 16 elements in 11 files, and a
 * hand-rolled `rounded-lg border border-border-subtle bg-card` repeated 39
 * times across 19 feature files — 8 of them in `Settings.tsx` alone, as one
 * identical string. 55 card surfaces, two spellings, no decision.
 * `components/ui/card-adoption.test.tsx` is what keeps the count at one.
 *
 * The merge was visually neutral, which is worth stating because it is the
 * only reason it could be done in one pass rather than negotiated per screen:
 * `--border` and `--border-subtle` hold the SAME value in both themes
 * (`220 14% 90%` / `224 14% 16%`), as do `--card-foreground` and `--foreground`
 * (`224 30% 12%` / `220 14% 92%`). The two recipes rendered identically; they
 * differed only in which token they named — and `--border-subtle` is the one
 * the majority named, so it is the one that survived. Should those tokens ever
 * diverge, that is a design decision about cards, and this is now the single
 * line where it gets made.
 *
 * `text-card-foreground` is the one class in the merged base that the majority
 * spelling did NOT carry, so token equality alone does not discharge it — the
 * 39 hand-rolled surfaces set no colour at all and inherited one. Two scans
 * closed that gap rather than assuming it (Rupert #1052 r0): no migrated card
 * has an ancestor setting a text colour, in its own file OR in whatever mounts
 * it, and `globals.css` declares no element-level `a { color }` for the two
 * `cardVariants()` `<Link>`s to now beat. So the class lands on elements that
 * were already inheriting `--foreground` from `body`, and the "no pixels
 * change" claim holds for it too. If a colour-setting wrapper ever appears
 * above a card, this is the line that stops being true.
 *
 * Every inset below is `p-card`, which resolves to `--card-pad` and therefore
 * to ONE value per density (U2.1). It used to be a literal `p-6` here while
 * feature code hand-rolled `p-3`/`p-4`/`p-5`/`p-6` around the same recipe —
 * five paddings with nothing governing any of them, and a `[data-density]`
 * mode that reached the AG-Grid theme and nothing else.
 *
 * Do not reintroduce a literal padding utility on these three. A caller that
 * genuinely needs a different inset passes it through `className` (`cn()`
 * merges it), which keeps the exception at the call site where it is visible
 * rather than in the primitive where it becomes the default.
 */

/**
 * Exported for the three call sites `<Card>` cannot serve, because the element
 * is not this file's to render: `DihedralRMSDGrid`'s tiles are `motion.div`s
 * (framer-motion drives their entrance stagger and hover scale), and
 * `FlowLaunchPanel` and `AnalysisProjectRuns` are card-shaped `<Link>`s that
 * must stay `<a>` elements. All three take the recipe as a class string.
 *
 * That is still ONE recipe: what the adoption bar forbids is a second copy of
 * the string, not a second way to apply the first. Same shape as shadcn's
 * `buttonVariants` — and `card-adoption.test.tsx` asserts the two spellings
 * resolve identically, since a drift between them would quietly rebuild the
 * split this unit closed.
 */
export const cardVariants = cva(
  "rounded-lg border border-border-subtle bg-card text-card-foreground",
  {
    variants: {
      /**
       * Elevation is a variant and it defaults to `flat`, which is a reversal of
       * what this primitive used to do — `shadow-sm` was in the base string, so
       * every caller got elevation whether or not it had asked.
       *
       * `flat` is the default because it is what the app overwhelmingly IS: of
       * the 55 card surfaces this unit unified, 36 rendered no shadow. U1.2
       * says it directly — "do not add elevation where the app currently uses
       * borders. Total shadow usage is 18 and the flatness is partly
       * deliberate." A default that adds ink is the one that erodes silently,
       * because nobody writes `elevation="flat"` defensively; a default that
       * adds nothing makes every shadow in the app a choice somebody typed.
       *
       * So the 19 surfaces that DO render a shadow today say `raised`
       * explicitly, and this unit changed no pixels.
       */
      elevation: {
        flat: "",
        raised: "shadow-sm",
      },
    },
    defaultVariants: { elevation: "flat" },
  }
);

/**
 * The elements a card may render as — a closed set, per this repo's
 * "closed vocabularies are `Literal`, never free `str`" convention.
 *
 * A card is a surface treatment, not an element, and 20 of the 55 migrated
 * surfaces proved it — 14 `<section>`s, 3 `<fieldset>`s, 2 `<aside>`s and a
 * `<dl>`. `RunProvenance`'s grid IS the definition list; `AnalysisCompare`'s
 * `<legend>`s are only legal inside a `<fieldset>`; `Settings`' eight panels
 * are labelled landmarks. Forcing them all to `<div>` would have traded
 * correct semantics for adoption — a bad trade in a plan whose own contract
 * C3 is about accessibility.
 *
 * Deliberately NOT `keyof JSX.IntrinsicElements`: that would admit `<button>`
 * and `<a>`, and a card-shaped control wants the interactive element's own
 * focus and hover treatment, not a surface's. `RunArtifactCard` keeps its
 * `<Link>` INSIDE the card for exactly that reason, and the two links that
 * ARE the card take `cardVariants()` as a class string instead.
 *
 * The set is the four elements callers actually use, not every element one
 * might: an unused member is a design decision nobody made. Add one when a
 * call site needs it.
 */
type CardElement = "div" | "section" | "aside" | "dl" | "fieldset";

export interface CardProps extends HTMLAttributes<HTMLElement>, VariantProps<typeof cardVariants> {
  as?: CardElement;
}

export const Card = forwardRef<HTMLElement, CardProps>(
  ({ className, elevation, as: Component = "div", ...props }, ref) => (
    <Component
      // One `as` cast, at the one place the element type is dynamic. Each
      // branch of `CardElement` wants its own `HTMLElement` subtype for the
      // ref, and TypeScript cannot narrow a union of intrinsic components to
      // the matching one; `Ref<never>` is the honest spelling of "whichever
      // this is". Callers stay fully typed — `as` is a closed union, so a typo
      // is a compile error.
      //
      // Two limitations it does buy, latent rather than broken because no call
      // site hits either today (Rupert #1052 r0). A `ref` is typed
      // `HTMLElement` whatever `as` says, so `<Card as="dl" ref={r}>` will not
      // catch a `dl`-only member at compile time; and props are
      // `HTMLAttributes<HTMLElement>`, so `fieldset`'s `disabled`/`form`/`name`
      // cannot be passed even though the element accepts them. A caller that
      // needs either should render the element itself with `cardVariants()` —
      // that is what the escape hatch is for. Making `as` generic over
      // `ComponentPropsWithRef` would remove both, and is worth doing the day
      // a call site needs it rather than speculatively.
      ref={ref as Ref<never>}
      className={cn(cardVariants({ elevation }), className)}
      {...props}
    />
  )
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-card", className)} {...props} />
  )
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-2xl font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-card pt-0", className)} {...props} />
  )
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-card pt-0", className)} {...props} />
  )
);
CardFooter.displayName = "CardFooter";
